import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './styles.css';

export default function Contact() {
  // Your Google Apps Script URL
  const WEB_APP_URL = 'https://script.google.com/macros/s/AKfycbwLwO9M55Mnp5o6GWkI1PiL4xa90AMDzx2n3k9xYdOiU4VKqiXXAXZvGDOkHGZw5KEf/exec';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Service options matching your spreadsheet
  const serviceOptions = [
    'system setup',
    'Tech support',
    'web/ app development',
    'Automation',
    'Consultation',
    'Lan/ wan setup "networking"',
    'Security setup + virus protection',
    'Data security & data recovery',
    'Other:'
  ];

  // Check URL for submitted parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('submitted') === 'true') {
      setFormSubmitted(true);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const formPayload = new URLSearchParams();
    formPayload.append('name', formData.name);
    formPayload.append('email', formData.email);
    formPayload.append('service', formData.service);
    formPayload.append('message', formData.message);

    try {
      // Send to Google Apps Script
      await fetch(WEB_APP_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formPayload.toString()
      });
      
      // Show success message
      setFormSubmitted(true);
      window.history.replaceState({}, '', '?submitted=true');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        service: '',
        message: ''
      });

    } catch (error) {
      console.error('Submission error:', error);
      setError('Failed to send. Please email us directly or try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Manual skip function
  const manuallyRemoveForm = () => {
    setFormSubmitted(true);
    window.history.replaceState({}, '', '?submitted=true');
  };

  // Structured Data for Contact Page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Raffcodes",
    "description": "Get in touch with Raffcodes for IT solutions, full-stack development, and tech support in Nairobi, Kenya.",
    "url": "https://raffcodes.tech/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "Raffcodes",
      "url": "https://raffcodes.tech",
      "telephone": "+254780362819",
      "email": "rafffnixx@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Munyu Road",
        "addressLocality": "Nairobi",
        "addressCountry": "Kenya"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+254780362819",
        "contactType": "Customer Service",
        "availableLanguage": ["English", "Swahili"]
      },
      "sameAs": [
        "https://twitter.com/raffcodes",
        "https://facebook.com/raffcodes",
        "https://instagram.com/_raffcodes",
        "https://t.me/raffcodes"
      ]
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Contact Raffcodes | IT Solutions & Full-Stack Development Nairobi</title>
        <meta name="description" content="Get in touch with Raffcodes for professional IT solutions, full-stack development, system setup, and tech support in Nairobi, Kenya. Call 0780 362 819 or email rafffnixx@gmail.com." />
        <meta name="keywords" content="contact Raffcodes, IT support Nairobi, web development Kenya, tech support, system setup, full-stack developer, get in touch" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://raffcodes.tech/contact" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Contact Raffcodes | IT Solutions & Full-Stack Development" />
        <meta property="og:description" content="Get in touch with Raffcodes for professional IT solutions, full-stack development, and tech support in Nairobi, Kenya." />
        <meta property="og:url" content="https://raffcodes.tech/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://raffcodes.tech/images/contact-og.jpg" />
        <meta property="og:site_name" content="Raffcodes" />
        <meta property="og:locale" content="en_KE" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Raffcodes | IT Solutions & Full-Stack Development" />
        <meta name="twitter:description" content="Get in touch for professional IT solutions and development services in Kenya." />
        <meta name="twitter:image" content="https://raffcodes.tech/images/contact-og.jpg" />
        <meta name="twitter:site" content="@raffcodes" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="page contact">
        {/* Header section - Enhanced Tech Style */}
        <section className="contact-header">
          <div className="contact-header-content">
            <span className="header-tag">Get in Touch</span>
            <h2>Let's Build Something Great Together</h2>
            <p>
              Ready to transform your ideas into reality? We're here to help with 
              professional IT solutions and full-stack development.
            </p>
            <div className="header-contact-badge">
              <i className="fas fa-clock"></i>
              <span>Response within 24 hours</span>
            </div>
          </div>
        </section>

        {!formSubmitted ? (
          <div className="form-wrapper">
            {/* Form header with skip button */}
            <div className="form-header">
              <div className="form-header-left">
                <h3><i className="fas fa-paper-plane"></i> Start Your Project</h3>
                <p className="form-subtitle">Fill in the form and we'll get back to you within 24 hours.</p>
              </div>
              <button 
                onClick={manuallyRemoveForm}
                className="skip-form-btn"
              >
                <i className="fas fa-arrow-right"></i> Contact Directly
              </button>
            </div>
            
            {/* Custom form instead of Google Form iframe */}
            <div className="contact-form-container">
              <form onSubmit={handleSubmit} className="contact-form">
                {/* Error message display */}
                {error && (
                  <div className="error-message-container">
                    <i className="fas fa-exclamation-triangle"></i>
                    <p>{error}</p>
                  </div>
                )}

                {/* Form fields */}
                <div className="form-group">
                  <label htmlFor="name">
                    <i className="fas fa-user"></i> Full Names <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope"></i> Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                    placeholder="you@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="service">
                    <i className="fas fa-code"></i> Service Needed <span className="required">*</span>
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select a service...</option>
                    {serviceOptions.map((service, index) => (
                      <option key={index} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    <i className="fas fa-comment"></i> Message <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                    placeholder="Describe your project or requirements in detail..."
                  />
                </div>

                <div className="submit-group">
                  <button type="submit" className="submit-btn" disabled={submitting}>
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i> Send Message
                      </>
                    )}
                  </button>
                  
                  <p className="form-note">
                    <i className="fas fa-lock"></i> Your information is safe and secure.
                  </p>
                </div>
              </form>
            </div>
            
            {/* Form footer note */}
            <div className="form-footer">
              <p>
                <i className="fas fa-info-circle"></i>
                <strong>Need immediate help?</strong> Call us at <a href="tel:+254780362819">0780 362 819</a> 
                or message us on <a href="https://wa.me/254780362819" target="_blank" rel="noopener noreferrer">WhatsApp</a>.
              </p>
            </div>
          </div>
        ) : (
          /* Success message - Enhanced */
          <div className="form-success-section">
            <div className="form-success-message">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3>Message Sent Successfully! 🎉</h3>
              <p>Thank you for reaching out to Raffcodes. We'll get back to you within 24 hours.</p>
              <div className="success-contact-info">
                <div className="info-card">
                  <i className="fas fa-phone"></i>
                  <div>
                    <strong>Need immediate assistance?</strong>
                    <p>Call us at <a href="tel:+254780362819">0780 362 819</a></p>
                  </div>
                </div>
                <div className="info-card">
                  <i className="fab fa-whatsapp"></i>
                  <div>
                    <strong>WhatsApp</strong>
                    <p>
                      <a href="https://wa.me/254780362819" target="_blank" rel="noopener noreferrer">
                        Chat with us now
                      </a>
                    </p>
                  </div>
                </div>
              </div>
              <a href="/" className="back-home-btn">
                <i className="fas fa-arrow-left"></i> Back to Home
              </a>
            </div>
          </div>
        )}

        {/* Contact details section - Enhanced Tech Style */}
        <section className="contact-details">
          <div className="contact-details-grid">
            <div className="contact-info-card">
              <h3><i className="fas fa-info-circle"></i> Contact Information</h3>
              <div className="contact-item">
                <div className="contact-icon-wrapper">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div>
                  <strong>Location</strong>
                  <p>Munyu Road, Nairobi, Kenya</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon-wrapper">
                  <i className="fas fa-phone"></i>
                </div>
                <div>
                  <strong>Phone</strong>
                  <p><a href="tel:+254780362819">0780 362 819</a></p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon-wrapper">
                  <i className="fas fa-envelope"></i>
                </div>
                <div>
                  <strong>Email</strong>
                  <p><a href="mailto:rafffnixx@gmail.com">rafffnixx@gmail.com</a></p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon-wrapper">
                  <i className="fas fa-clock"></i>
                </div>
                <div>
                  <strong>Working Hours</strong>
                  <p>Mon - Fri: 8:00 AM - 6:00 PM</p>
                  <p className="hours-sub">Sat: 9:00 AM - 2:00 PM</p>
                </div>
              </div>
            </div>

            <div className="social-info-card">
              <h3><i className="fas fa-share-alt"></i> Connect With Us</h3>
              <p className="social-subtitle">Follow us on social media for updates and tech tips</p>
              <div className="social-links">
                <a href="https://twitter.com/raffcodes" target="_blank" rel="noopener noreferrer" className="social-link twitter">
                  <i className="fab fa-twitter"></i>
                  <span>Twitter</span>
                </a>
                <a href="https://facebook.com/raffcodes" target="_blank" rel="noopener noreferrer" className="social-link facebook">
                  <i className="fab fa-facebook-f"></i>
                  <span>Facebook</span>
                </a>
                <a href="https://instagram.com/_raffcodes" target="_blank" rel="noopener noreferrer" className="social-link instagram">
                  <i className="fab fa-instagram"></i>
                  <span>Instagram</span>
                </a>
                <a href="https://t.me/raffcodes" target="_blank" rel="noopener noreferrer" className="social-link telegram">
                  <i className="fab fa-telegram-plane"></i>
                  <span>Telegram</span>
                </a>
                <a href="https://wa.me/254780362819" target="_blank" rel="noopener noreferrer" className="social-link whatsapp">
                  <i className="fab fa-whatsapp"></i>
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}